import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PROOF } from "@/data/content";

const card = {
  hidden: { opacity: 0, y: 36 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 },
  }),
};

export const ProofGallery = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="proof"
      data-testid="proof-section"
      className="px-6 md:px-12 py-24 md:py-36 bg-ink text-white"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-lime">
              (01) — The evidence
            </span>
            <h2 className="mt-4 font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-[16ch]">
              Results, not promises.
            </h2>
          </div>
          <p className="max-w-sm text-white/50 text-base md:text-lg leading-relaxed">
            Real organic growth pulled straight from client dashboards — Google Search
            Console, GA4 and Semrush. Tap any screenshot to view it full size.
          </p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]">
          {PROOF.map((p, i) => (
            <motion.button
              key={p.src}
              type="button"
              custom={i}
              variants={card}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              onClick={() => setActive(p)}
              data-testid={`proof-card-${i}`}
              className="group mb-4 md:mb-5 block w-full break-inside-avoid overflow-hidden rounded-lg border border-white/10 bg-white text-left transition-colors duration-500 hover:border-lime cursor-pointer"
            >
              <div className="overflow-hidden">
                <img
                  src={p.src}
                  alt={`${p.source} — ${p.headline}`}
                  loading="lazy"
                  className="w-full block transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="bg-white px-5 py-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-forest">
                  {p.source}
                </div>
                <div className="mt-1.5 font-display font-bold text-lg tracking-tight text-ink leading-tight">
                  {p.headline}
                </div>
                <div className="mt-1 text-sm text-ink/50">{p.sub}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-4xl w-[95vw] p-0 overflow-hidden bg-white border-none"
          data-testid="proof-lightbox"
        >
          {active && (
            <>
              <DialogTitle className="sr-only">{active.headline}</DialogTitle>
              <img
                src={active.src}
                alt={`${active.source} — ${active.headline}`}
                className="w-full block"
              />
              <div className="px-6 py-4 border-t border-hairline">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-forest">
                  {active.source}
                </div>
                <div className="mt-1 font-display font-bold text-xl text-ink">
                  {active.headline}
                </div>
                <div className="text-sm text-ink/50">{active.sub}</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
